import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, CalendarClock, Clock3, Filter, ListTodo, Search, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell } from 'recharts';

import api, { getStoredUser } from '../api';
import GoalDetailView from './GoalDetailView';
import GoalNotificationsPanel from './GoalNotificationsPanel';
import { GOALS_TEXT_SCOPE_STYLES, GOAL_STATUS_COLORS, formatDate, formatNumber, getModuleMeta, getVisibleModuleMetrics, prettyLabel, statusStyle } from './shared';

const Shell = styled.div`
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
`;

const Page = styled.div`
  display: grid;
  gap: 22px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #dbe5f1;
  border-radius: 24px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
`;

const Hero = styled(Card)`
  overflow: hidden;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  gap: 22px;
  padding: 30px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const BackButton = styled.button`
  border: 1px solid #c9d8ea;
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MiniMetric = styled.div`
  border-radius: 20px;
  padding: 16px;
  border: 1px solid #d9ebfb;
  background: rgba(255, 255, 255, 0.92);
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.8fr);
  gap: 22px;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled(Card)`
  padding: 22px;
`;

const GoalCard = styled.div`
  border: 1px solid #dbe5f1;
  border-radius: 22px;
  padding: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
`;

const ProgressRail = styled.div`
  width: 100%;
  height: 10px;
  background: #e8eef6;
  border-radius: 999px;
  overflow: hidden;
`;

const ActionButton = styled.button`
  border: ${(props) => (props.$primary ? '0' : '1px solid #c9d8ea')};
  background: ${(props) => (props.$primary ? '#2563eb' : '#ffffff')};
  color: ${(props) => (props.$primary ? '#ffffff' : '#0f172a')};
  border-radius: 12px;
  padding: 11px 16px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const EmptyCard = styled.div`
  border: 1px dashed #c7d6e6;
  border-radius: 18px;
  padding: 18px;
  background: #f8fbff;
`;

export default function ModuleGoalsDashboard({ moduleName, backPath = '' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [filters, setFilters] = useState({ search: '', status: '', priority: '' });

  const moduleMeta = getModuleMeta(moduleName);

  const loadData = async () => {
    try {
      const response = await api.get(`/goals/dashboard/module/${moduleName}`);
      setData(response.data || null);
      setCurrentUser(getStoredUser());
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    loadData();
  }, [moduleName]);

  const filteredGoals = useMemo(() => {
    const source = data?.goals || [];
    return source.filter((goal) => {
      const matchesSearch = !filters.search || `${goal.title} ${goal.description || ''}`.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = !filters.status || goal.status === filters.status;
      const matchesPriority = !filters.priority || Number(goal.priority || 3) === Number(filters.priority);
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [data?.goals, filters]);

  const highlightedGoals = useMemo(
    () => filteredGoals.filter((goal) => goal.status === 'OVERDUE' || Number(goal.priority || 3) >= 4),
    [filteredGoals],
  );

  if (selectedGoalId) {
    return (
      <GoalDetailView
        goalId={selectedGoalId}
        canManage
        initialTab={selectedTab}
        onClose={() => {
          setSelectedGoalId(null);
          setSelectedTab('overview');
        }}
        onRefresh={loadData}
      />
    );
  }

  if (!data) {
    return <SectionCard className="goals-text-scope"><style>{GOALS_TEXT_SCOPE_STYLES}</style>Loading goals workspace...</SectionCard>;
  }

  return (
    <Shell className="goals-text-scope">
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>
      <Page>
        <Hero style={{ background: `radial-gradient(circle at top right, ${moduleMeta.color}22, transparent 28%), linear-gradient(135deg, ${moduleMeta.accent} 0%, #f8fbff 45%, #ffffff 100%)` }}>
          <HeroGrid>
            <div>
              {backPath ? (
                <BackButton type="button" onClick={() => navigate(backPath)}>
                  <ArrowLeft size={16} />
                  Back To {prettyLabel(moduleName)}
                </BackButton>
              ) : null}
              <div style={{ marginTop: backPath ? 18 : 0, fontSize: 12, fontWeight: 900, letterSpacing: '0.12em', color: moduleMeta.color }}>
                {moduleMeta.icon} {prettyLabel(moduleName)} GOALS & TASKS
              </div>
              <div style={{ marginTop: 10, fontSize: 36, fontWeight: 900, lineHeight: 1.1 }}>
                Clear module-specific work without cross-department clutter
              </div>
              <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.7, maxWidth: 760 }}>
                This workspace shows {prettyLabel(moduleName)} goals plus shared company-wide goals. Each card highlights the metrics this team actually cares about, so people do not have to scan generic forms or unrelated tasks.
              </div>

              <HeroStats>
                <MiniMetric>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Visible Goals</div>
                    <Target size={18} color={moduleMeta.color} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900 }}>{filteredGoals.length}</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>Filtered module goals currently in this workspace.</div>
                </MiniMetric>
                <MiniMetric>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Pending Tasks</div>
                    <ListTodo size={18} color="#0f766e" />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900 }}>{data.pendingTasksCount || 0}</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>Tasks still waiting for proof or completion.</div>
                </MiniMetric>
                <MiniMetric>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Needs Attention</div>
                    <Clock3 size={18} color="#dc2626" />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900 }}>{highlightedGoals.length}</div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>Overdue or high-priority goals that should be reviewed first.</div>
                </MiniMetric>
              </HeroStats>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <MiniMetric>
                <div style={{ fontSize: 12, fontWeight: 800 }}>Workspace Owner</div>
                <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900 }}>{currentUser?.username || 'Manager'}</div>
                <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
                  Open any goal card below to move straight into progress updates, proof photos, or task execution.
                </div>
              </MiniMetric>
              <MiniMetric>
                <div style={{ fontSize: 12, fontWeight: 800 }}>How This Page Helps</div>
                <div style={{ marginTop: 10, display: 'grid', gap: 8, fontSize: 13, lineHeight: 1.6 }}>
                  <div>1. Filter only the work you need</div>
                  <div>2. See module KPIs on each card</div>
                  <div>3. Open tasks or update proof quickly</div>
                  <div>4. Keep shared goals visible without noise</div>
                </div>
              </MiniMetric>
            </div>
          </HeroGrid>
        </Hero>

        <SectionGrid>
          <SectionCard>
            <div style={{ fontSize: 20, fontWeight: 900 }}>Progress Snapshot</div>
            <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>
              Status mix and monthly trend for {prettyLabel(moduleName)} goals and shared company-wide goals.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.statusBreakdown || []} dataKey="count" nameKey="status" outerRadius={92} label>
                      {(data.statusBreakdown || []).map((entry) => (
                        <Cell key={entry.status} fill={(GOAL_STATUS_COLORS[entry.status] || GOAL_STATUS_COLORS.ACTIVE).text} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={data.progressTrend || []}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="avg_progress" stroke={moduleMeta.color} strokeWidth={3} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </SectionCard>

          <GoalNotificationsPanel moduleName={moduleName} />
        </SectionGrid>

        <SectionGrid>
          <SectionCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>My {prettyLabel(moduleName)} Tasks & Goals</div>
                <div style={{ marginTop: 6, fontSize: 14 }}>Filter the list and open the exact goal or task flow you need.</div>
              </div>
              <div style={{ padding: '8px 12px', borderRadius: 999, background: moduleMeta.accent, border: `1px solid ${moduleMeta.border}`, color: moduleMeta.color, fontWeight: 800 }}>
                {filteredGoals.length} results
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) repeat(2, minmax(160px, 0.6fr))', gap: 12, marginBottom: 18 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Search</span>
                <div style={{ position: 'relative' }}>
                  <Search size={16} color="#64748b" style={{ position: 'absolute', left: 12, top: 13 }} />
                  <input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder={`Search ${prettyLabel(moduleName)} goals`} style={{ width: '100%', padding: '12px 12px 12px 38px', borderRadius: 14, border: '1px solid #cbd5e1' }} />
                </div>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Status</span>
                <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1' }}>
                  <option value="">All Status</option>
                  {['DRAFT', 'ACTIVE', 'ON_TRACK', 'AT_RISK', 'OVERDUE', 'COMPLETED', 'CANCELLED'].map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 13 }}>Priority</span>
                <select value={filters.priority} onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))} style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1' }}>
                  <option value="">All Priority</option>
                  {[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              {filteredGoals.length ? filteredGoals.map((goal) => {
                const tone = statusStyle(goal.status);
                const metricChips = getVisibleModuleMetrics(goal, 4);
                const priorityLabel = Number(goal.priority || 3) >= 4 ? 'High Priority' : Number(goal.priority || 3) === 3 ? 'Medium' : 'Low';
                return (
                  <GoalCard key={goal.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ maxWidth: 760 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ padding: '6px 10px', borderRadius: 999, background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`, fontWeight: 800, fontSize: 12 }}>{prettyLabel(goal.status)}</span>
                          <span style={{ padding: '6px 10px', borderRadius: 999, background: Number(goal.priority || 3) >= 4 ? '#fee2e2' : '#fef3c7', color: Number(goal.priority || 3) >= 4 ? '#991b1b' : '#92400e', fontWeight: 800, fontSize: 12 }}>
                            {priorityLabel}
                          </span>
                        </div>
                        <div style={{ marginTop: 12, fontSize: 22, fontWeight: 900 }}>{goal.title}</div>
                        <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>{goal.description || 'No description provided for this goal yet.'}</div>
                      </div>
                      <div style={{ minWidth: 220, border: '1px solid #dbe5f1', borderRadius: 18, padding: 14, background: '#fff' }}>
                        <div style={{ fontSize: 12, fontWeight: 800 }}>Assigned Module</div>
                        <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900 }}>{prettyLabel(goal.assigned_module)}</div>
                        <div style={{ marginTop: 10, fontSize: 12, fontWeight: 800 }}>Due Date</div>
                        <div style={{ marginTop: 6, fontWeight: 900 }}>{formatDate(goal.end_date)}</div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 16 }}>
                      <div><strong>{formatNumber(goal.current_value)}</strong><br />Current Value</div>
                      <div><strong>{formatNumber(goal.target_value)}</strong><br />Target Value</div>
                      <div><strong>{goal.days_remaining}</strong><br />Days Left</div>
                      <div><strong>{goal.assigned_to_user_id || 'Shared'}</strong><br />Owner</div>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8, fontSize: 13, fontWeight: 800 }}>
                        <span>Completion Progress</span>
                        <span>{formatNumber(goal.completion_percentage)}%</span>
                      </div>
                      <ProgressRail>
                        <div style={{ width: `${Math.min(100, Math.max(0, Number(goal.completion_percentage || 0)))}%`, height: '100%', background: `linear-gradient(90deg, ${moduleMeta.color}88 0%, ${moduleMeta.color} 100%)` }} />
                      </ProgressRail>
                    </div>

                    <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                      <div style={{ fontWeight: 900, marginBottom: 10 }}>{prettyLabel(goal.assigned_module)} Metrics</div>
                      {metricChips.length ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                          {metricChips.map((metric) => (
                            <div key={metric.key} style={{ border: '1px solid #dbe5f1', borderRadius: 16, background: '#ffffff', padding: 12 }}>
                              <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>{metric.label}</div>
                              <div style={{ marginTop: 6, fontSize: 15, fontWeight: 900 }}>{metric.value}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: '#64748b' }}>No module-specific metrics captured for this goal yet.</div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                      <ActionButton $primary onClick={() => { setSelectedGoalId(goal.id); setSelectedTab('overview'); }}>
                        View Details
                        <ArrowRight size={15} />
                      </ActionButton>
                      <ActionButton onClick={() => { setSelectedGoalId(goal.id); setSelectedTab('tasks'); }}>
                        Open Tasks
                      </ActionButton>
                    </div>
                  </GoalCard>
                );
              }) : (
                <EmptyCard>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 900, fontSize: 18 }}>
                    <Filter size={18} color={moduleMeta.color} />
                    No goals match this filter
                  </div>
                  <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
                    Try clearing the filters or wait for a {prettyLabel(moduleName)} goal to be assigned. Only this module and `All Modules` goals appear here.
                  </div>
                </EmptyCard>
              )}
            </div>
          </SectionCard>

          <SectionCard style={{ background: 'linear-gradient(180deg,#f8fbff 0%,#ffffff 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <CalendarClock size={18} color={moduleMeta.color} />
              <div style={{ fontSize: 20, fontWeight: 900 }}>Upcoming Deadlines</div>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {(data.upcomingDeadlines || []).length ? data.upcomingDeadlines.map((goal) => (
                <div key={goal.id} style={{ border: '1px solid #dbe5f1', borderRadius: 18, padding: 16, background: '#ffffff' }}>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>{goal.title}</div>
                  <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6 }}>
                    Due {formatDate(goal.end_date)} | {goal.days_remaining} days left | {formatNumber(goal.completion_percentage)}% complete
                  </div>
                  <ActionButton $primary style={{ marginTop: 12 }} onClick={() => { setSelectedGoalId(goal.id); setSelectedTab('overview'); }}>
                    Open Goal
                  </ActionButton>
                </div>
              )) : (
                <EmptyCard>No upcoming deadlines for this module.</EmptyCard>
              )}
            </div>
          </SectionCard>
        </SectionGrid>
      </Page>
    </Shell>
  );
}
